const DATA_FILE_TITLE = "fileTitle";
const DATA_LINK_LABEL = "linkLabel";
const FALLBACK_INTERVAL_TIMEOUT_MS = 300;

/**
 * Convert SVG QR code to downloadable PNG and
 * wrap its container with download link
 *
 * Some browsers may not fire "load" event when SVG loads (Firefox so far),
 * so as a workaround, there is an interval, which is checking for SVG height
 * and in case load event wasn´t fired interval will do the same thing
 * -> create downloadable .png file from provided svg
 *
 * @param container {HTMLElement} container element
 */
const DownloadableQrCode = (container: HTMLElement): void => {
  const svgEl: SVGElement = container.querySelector("svg");

  if (!svgEl) return;

  /* Helper variables in case "load" event is not fired */
  let isSvgLoaded = false;
  let fallbackInterval: number;

  const fileTitle = container.dataset[DATA_FILE_TITLE];
  const linkLabel = container.dataset[DATA_LINK_LABEL];

  /**
   * Clear interval and set helper loaded variable to true to prevent
   * multiple .png file convertions
   */
  const stopInterval = (): void => {
    isSvgLoaded = true;

    window.clearInterval(fallbackInterval);
  };

  const createDownloadLink = (imgURL: string): void => {
    const linkEl = document.createElement("a");

    linkEl.download = fileTitle;
    linkEl.href = imgURL;

    linkEl.setAttribute("aria-label", linkLabel);
    linkEl.classList.add("block", "w-fit");

    // Insert div container into created link
    container.parentNode.insertBefore(linkEl, container);
    // Append link into page
    linkEl.appendChild(container);
  };

  const convertSvgToPng = (): void => {
    /* Prevent multiple creations of downloadable png file */
    if (isSvgLoaded) return;

    stopInterval();

    const img = new Image();
    const serializer = new XMLSerializer();
    // Constructs a string representing SVG in XML form.
    const svgString = serializer.serializeToString(svgEl);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      // Ideal PNG size should be 800x800
      const idealPngSize = 800;

      const { height: svgElHeight, width: svgElWidth } = svgEl.getBoundingClientRect();
      const longerSvgSize = Math.max(svgElHeight, svgElWidth);

      const multiplaier = Math.ceil(idealPngSize / longerSvgSize);

      /**
       * QR code is not exact rectangle due to overflowed bottom text,
       * so count to it from actual SVG element size
       * and try to be as close as possible to ideal size.
       */
      const canvasWidth = svgElWidth * multiplaier;
      const canvasHeight = svgElHeight * multiplaier;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const context = canvas.getContext("2d");
      context.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const imgURL = canvas.toDataURL("image/png");

      createDownloadLink(imgURL);
    };

    // Creates a Base64-encoded ASCII string from a binary string
    img.src = `data:image/svg+xml;base64,${window.btoa(svgString)}`;
  };

  /**
   * Check for SVG height and decide if convertSvgToPng should be called
   */
  const handleInterval = (): void => {
    if (svgEl.clientHeight === 0) return;

    convertSvgToPng();
  };

  /* Load listener + fallback interval */
  svgEl.addEventListener("load", convertSvgToPng);
  fallbackInterval = window.setInterval(handleInterval, FALLBACK_INTERVAL_TIMEOUT_MS);
};

export default DownloadableQrCode;
